import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { cancelCriterionCReassessmentRequest, confirmCriterionC } from 'api/criterionC'
import AssessIcon from 'assets/icons/assess-icon'
import CalendarIcon from 'assets/icons/calendar-icon'
import CancelIcon from 'assets/icons/cancel-icon16px'
import ConfirmIcon from 'assets/icons/confirm-icon'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import ReassessIcon from 'assets/icons/reassess-icon'
import RejectIcon from 'assets/icons/reject-icon'
import RemoveIcon from 'assets/icons/remove-icon'
import Button from 'components/Button'
import FlowRequestModal from 'components/FlowRequestModal'
import Modal from 'components/Modal'
import Tooltip from 'components/Tooltip'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { reassessmentTriggerReasons } from 'constants/reassessmentTriggerReasons'
import { INDIVIDUAL_ROLE_ID, SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID } from 'constants/roles'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID } from 'constants/siteStatuses'
import ActionsMenu from 'features/ActionMenu'
import PulsingNotification from 'features/PulsingNotification'
import { ProfileContext } from 'providers/profile'
import { SiteContext } from 'providers/site'
import { FormattedMessage } from 'react-intl'
import { showErrorToast } from 'utils/toast'
import { getCriterionCId, hasQualifyingReason } from 'utils/criterionCHelpers'
import {
  EcosystemActionMenuWrapper,
  EcosystemCalendarDateText, EcosystemCalendarDivider, EcosystemCalendarIconWrapper,
  EcosystemConfirmDeclineIcon,
  EcosystemHeaderIcon,
  EcosystemHeaderIconWrapper,
  EcosystemHeaderNavbarHalfSide,
  EcosystemHeaderNavbarIconWrapper,
  EcosystemHeaderNavbarWrapper,
  EcosystemHeaderTitle, EcosystemLastReassessmentCalendarWrapper,
  EcosystemsCancelIconWrapper,
  EcosystemToggleWrapper,
  EcoSystemValidationIcon, EcosystemValidationIconWrapper
} from '../ecosystemData/style'

const formattedRejectIconTooltipHint = (item) => {
  return (item.trigger_reason_id || []).map((element) => {
    return reassessmentTriggerReasons.find((elementId) => {
      return elementId.id === Number(element)
    })?.name
  }).filter(Boolean)
}


const tooltipXOffset = 0
const tooltipYOffset = -16
const dateOptions = {
  year: 'numeric',
  month: 'short',
  weekday: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: true
}

const getReassessmentActionId = (oldTriggers, newTriggers) => {
  if (oldTriggers) return 'reassess'
  if (newTriggers) return 'assess'
  return 'reassess'
}

const criterionCItemPropType = PropTypes.shape({
  site_ecointegrity_history_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site_ecointegrity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site_history_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updated_at: PropTypes.string,
  trigger_reason_id: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  last_reassessment_date: PropTypes.string,
  is_approved_by_secretariat: PropTypes.bool,
  EcoRegion: PropTypes.shape({
    name: PropTypes.string
  })
})

const ReassessmentDate = ({ hasDateToShow, dateToShow }) => {
  if (!hasDateToShow) {
    return null
  }

  return (
    <Tooltip
      style={{ zIndex: 1000 }}
      placement='top'
      trigger={['hover']}
      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
      overlay={{ label: 'lastTimeReassessed' }}
    >
      <EcosystemLastReassessmentCalendarWrapper>
        <EcosystemCalendarIconWrapper>
          <CalendarIcon />
        </EcosystemCalendarIconWrapper>
        <EcosystemCalendarDateText>
          {dateToShow.toLocaleDateString('en-US', dateOptions)}
        </EcosystemCalendarDateText>
        <EcosystemCalendarDivider />
      </EcosystemLastReassessmentCalendarWrapper>
    </Tooltip>
  )
}

const RemoveCriterionCButton = ({ item, openRemoveCriterionCModal, isDraftAndEnabled, isReassessmentMode }) => {
  if (!isDraftAndEnabled) {
    return null
  }

  if (isReassessmentMode && item.last_reassessment_date) {
    return null
  }

  return (
    <EcosystemHeaderNavbarIconWrapper
      delete={isReassessmentMode}
      data-testid='remove-button'
      onClick={(e) => {
        e.stopPropagation()
        openRemoveCriterionCModal(item)
      }}
    >
      <RemoveIcon />
    </EcosystemHeaderNavbarIconWrapper>
  )
}

const QualificationIndicator = ({ qulifyes, item }) => (
  <EcosystemConfirmDeclineIcon type={qulifyes ? 'confirmed' : 'rejected'}>
    {qulifyes ? (
      <Tooltip
        style={{ zIndex: 1000 }}
        placement='top'
        trigger={['hover']}
        align={{ offset: [tooltipXOffset, tooltipYOffset] }}
        overlay={{ label: 'qualifies' }}
      >
        <div>
          <ConfirmIcon />
        </div>
      </Tooltip>
    ) : (
      <Tooltip
        style={{ zIndex: 1000 }}
        placement='top'
        trigger={['hover']}
        align={{ offset: [tooltipXOffset, tooltipYOffset] }}
        overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(item).join(', ') }}
      >
        <div>
          <RejectIcon />
        </div>
      </Tooltip>
    )}
  </EcosystemConfirmDeclineIcon>
)

const HeaderActions = ({
  site,
  profile,
  item,
  isOpen,
  oldTriggers,
  newTriggers,
  toggleAccordion,
  openRemoveCriterionCModal,
  fieldsToCompleteNumber,
  actionMenuItems,
  disabled,
  setCriterionCToCancel,
  setIsCancelOpen,
  hasDateToShow,
  dateToShow,
  qulifyes
}) => {
  const isDraft = site.SiteStatusId === SITE_STATUS_DRAFT_ID
  const isNominatedSecretariat = site.SiteStatusId === SITE_STATUS_NOMINATED_ID &&
    (profile.RoleId === SECRETARIAT_ROLE_ID || profile.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID)
  const isDraftAndEnabled = isDraft && !disabled
  const hasIncompleteFields = fieldsToCompleteNumber.length > 0
  const reassessmentActionId = getReassessmentActionId(oldTriggers, newTriggers)

  const renderValidationIndicator = () => {
    if (!hasIncompleteFields) {
      return null
    }

    return (
      <EcosystemValidationIconWrapper>
        <EcoSystemValidationIcon>
          <PulsingNotification number={fieldsToCompleteNumber.length} />
        </EcoSystemValidationIcon>
      </EcosystemValidationIconWrapper>
    )
  }

  const renderReassessmentActions = () => (
    <>
      <ReassessmentDate hasDateToShow={hasDateToShow} dateToShow={dateToShow} />
      <QualificationIndicator qulifyes={qulifyes} item={item} />
      {isDraft ? (
        <>
          <Button
            isDisabled={isOpen}
            type={isOpen ? 'defaultDisabled' : ' '}
            height='32px'
            marginTop='0'
            onClick={toggleAccordion}
          >
            <ReassessIcon />
            <FormattedMessage id={reassessmentActionId} />
          </Button>
          {oldTriggers && (
            <EcosystemsCancelIconWrapper onClick={(e) => {
              e.stopPropagation()
              setCriterionCToCancel(item)
              setIsCancelOpen(true)
            }}>
              <CancelIcon />
            </EcosystemsCancelIconWrapper>
          )}
        </>
      ) : (
        <EcosystemToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
          <FormattedMessage id='expand' />
          {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
        </EcosystemToggleWrapper>
      )}
      <RemoveCriterionCButton
        item={item}
        openRemoveCriterionCModal={openRemoveCriterionCModal}
        isDraftAndEnabled={isDraftAndEnabled}
        isReassessmentMode={true}
      />
    </>
  )

  const renderStandardActions = () => (
    <>
      {isDraft ? (
        <Button
          height='32px'
          onClick={toggleAccordion}
          marginTop='0'
          isDisabled={isOpen}
          type={isOpen ? 'defaultDisabled' : ''}
        >
          <AssessIcon />
          <FormattedMessage id={reassessmentActionId} />
        </Button>
      ) : (
        <>
          <ReassessmentDate hasDateToShow={hasDateToShow} dateToShow={dateToShow} />
          <QualificationIndicator qulifyes={qulifyes} item={item} />
          <EcosystemToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
            <FormattedMessage id='expand' />
            {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
          </EcosystemToggleWrapper>
        </>
      )}
      <RemoveCriterionCButton
        item={item}
        openRemoveCriterionCModal={openRemoveCriterionCModal}
        isDraftAndEnabled={isDraftAndEnabled}
        isReassessmentMode={false}
      />
      {isNominatedSecretariat && (
        <EcosystemActionMenuWrapper>
          <ActionsMenu table={false} dropdownItems={actionMenuItems} margin='0' />
        </EcosystemActionMenuWrapper>
      )}
    </>
  )

  return (
    <>
      {site.reassessmentFlag ? renderReassessmentActions() : renderStandardActions()}
      {renderValidationIndicator()}
    </>
  )
}

const CriterionCAccordionHeader = ({ isOpen, item, openRemoveCriterionCModal, getCriterionCs, toggleAccordion, lastItem, newTriggers = false, oldTriggers = false }) => {
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const isIndividualUser = profile.Role.id === INDIVIDUAL_ROLE_ID
  const dateToShow = item.updated_at ? new Date(item.updated_at) : new Date()
  const hasDateToShow = Boolean(item.updated_at)
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [criterionCToCancel, setCriterionCToCancel] = useState(null)

  const handleCriterionCAction = async (isApproved) => {
    await confirmCriterionC(getCriterionCId(item), isApproved)
    await getCriterionCs()
  }

  const actionMenuItems = item.is_approved_by_secretariat ? [{
    icon: <RejectIcon />,
    title: 'rejectCriterionC',
    onClick: async () => {
      await handleCriterionCAction({ isApproved: false })
    },
    isDestructive: true
  }] : [{
    icon: <ConfirmIcon />,
    title: 'confirmCriterionC',
    onClick: async () => {
      await handleCriterionCAction({ isApproved: true })
    }
  }]

  const criterionCId = getCriterionCId(item)
  const validationCriterionC = siteProposalFieldValidation.criterionC[criterionCId]
    ? siteProposalFieldValidation.criterionC[criterionCId]
    : defaultSiteProposalValidationResults.criterionC

  const fieldsToCompleteNumber = Object.values(validationCriterionC).filter((element) => {
    return element === false
  })

  const qulifyes = hasQualifyingReason(item.trigger_reason_id)

  const cancelCriterionCsChanges = async (criterionC) => {
    if (!isIndividualUser) {
      showErrorToast('This action is not allowed.')
      return
    }
    const data = await cancelCriterionCReassessmentRequest({ siteId: criterionC.site_history_id, criterionCId: criterionC.site_ecointegrity_history_id, isCancelled: true })
    if (data.error) {
      return showErrorToast(data.error)
    }
    setIsCancelOpen(false)
    setCriterionCToCancel(null)
    toggleAccordion()
    await getCriterionCs()
  }

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qulifyes

  return (
    <>
      <EcosystemHeaderNavbarWrapper
        isOpen={isOpen}
        bottomBorder={isOpen || lastItem}
        data-testid='criterionC'
      >
        <EcosystemHeaderNavbarHalfSide onClick={toggleAccordion}>
          <EcosystemHeaderIconWrapper>
            <EcosystemHeaderIcon isRejected={!qulifyes} />
          </EcosystemHeaderIconWrapper>
          <EcosystemHeaderTitle
            isRejected={!qulifyes}
            data-testid='criterionC-name'
            isReassess={item.last_reassessment_date}
          >
            {item?.EcoRegion?.name}
          </EcosystemHeaderTitle>
        </EcosystemHeaderNavbarHalfSide>
        <EcosystemHeaderNavbarHalfSide>
          <HeaderActions
            site={site}
            profile={profile}
            item={item}
            isOpen={isOpen}
            oldTriggers={oldTriggers}
            newTriggers={newTriggers}
            toggleAccordion={toggleAccordion}
            openRemoveCriterionCModal={openRemoveCriterionCModal}
            fieldsToCompleteNumber={fieldsToCompleteNumber}
            actionMenuItems={actionMenuItems}
            disabled={disabled}
            setCriterionCToCancel={setCriterionCToCancel}
            setIsCancelOpen={setIsCancelOpen}
            hasDateToShow={hasDateToShow}
            dateToShow={dateToShow}
            qulifyes={qulifyes}
          />
        </EcosystemHeaderNavbarHalfSide>
      </EcosystemHeaderNavbarWrapper>
      <Modal
        width='416px'
        isOpen={isCancelOpen}
        toggleModal={setIsCancelOpen}
        modalTitleId='cancelCriterionCReassessment'
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsCancelOpen(false)
            setCriterionCToCancel(null)
          }}
          confirmHandler={() => {
            cancelCriterionCsChanges(criterionCToCancel)
          }}
          confirmBtnText='yesCancel'
          rejectBtnText='continueEditing'
          text={<FormattedMessage id='returnDiscardCriterionCChangesText' values={{ criterionCName: <b>{criterionCToCancel?.EcoRegion?.name}</b> }} />}
          item={criterionCToCancel}
          approve={true}
        />
      </Modal>
    </>
  )
}

export default CriterionCAccordionHeader

ReassessmentDate.propTypes = {
  hasDateToShow: PropTypes.bool.isRequired,
  dateToShow: PropTypes.instanceOf(Date).isRequired
}

RemoveCriterionCButton.propTypes = {
  item: criterionCItemPropType.isRequired,
  openRemoveCriterionCModal: PropTypes.func.isRequired,
  isDraftAndEnabled: PropTypes.bool.isRequired,
  isReassessmentMode: PropTypes.bool.isRequired
}

QualificationIndicator.propTypes = {
  qulifyes: PropTypes.bool.isRequired,
  item: criterionCItemPropType.isRequired
}

HeaderActions.propTypes = {
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  profile: PropTypes.shape({
    RoleId: PropTypes.number
  }).isRequired,
  item: criterionCItemPropType.isRequired,
  isOpen: PropTypes.bool,
  oldTriggers: PropTypes.bool,
  newTriggers: PropTypes.bool,
  toggleAccordion: PropTypes.func.isRequired,
  openRemoveCriterionCModal: PropTypes.func.isRequired,
  fieldsToCompleteNumber: PropTypes.arrayOf(PropTypes.bool).isRequired,
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.node,
    title: PropTypes.string,
    onClick: PropTypes.func,
    isDestructive: PropTypes.bool
  })).isRequired,
  disabled: PropTypes.bool.isRequired,
  setCriterionCToCancel: PropTypes.func.isRequired,
  setIsCancelOpen: PropTypes.func.isRequired,
  hasDateToShow: PropTypes.bool.isRequired,
  dateToShow: PropTypes.instanceOf(Date).isRequired,
  qulifyes: PropTypes.bool
}

CriterionCAccordionHeader.propTypes = {
  isOpen: PropTypes.bool,
  item: criterionCItemPropType.isRequired,
  openRemoveCriterionCModal: PropTypes.func.isRequired,
  getCriterionCs: PropTypes.func.isRequired,
  toggleAccordion: PropTypes.func.isRequired,
  lastItem: PropTypes.bool,
  newTriggers: PropTypes.bool,
  oldTriggers: PropTypes.bool
}